import React, { useEffect, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { languages } from "@/i18n/translations";
import { buildWhatsAppLink } from "@/lib/contact";
import { LogoLockup } from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS = [
  { key: "concept", href: "#concept" },
  { key: "menus", href: "#menu" },
  { key: "events", href: "#events" },
  { key: "reviews", href: "#reviews" },
  { key: "location", href: "#location" },
];

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];
  const waLink = buildWhatsAppLink(t.whatsappMessage);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "glass-header py-3 text-[#2D3748]"
          : "bg-gradient-to-b from-black/50 to-transparent py-5 text-white"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between gap-2 sm:gap-6">
        {/* Logo */}
        <a
          href="#top"
          data-testid="header-logo"
          className="flex items-center group"
        >
          <LogoLockup variant={scrolled ? "dark" : "light"} />
        </a>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-6 whitespace-nowrap">
          {NAV_LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-${l.key}`}
              className={`text-sm transition-colors duration-200 relative group ${
                scrolled
                  ? "text-[#2D3748] hover:text-[#0047AB]"
                  : "text-white/90 hover:text-[#D4AF37]"
              }`}
            >
              {t.nav[l.key]}
              <span className="absolute left-0 -bottom-1 w-0 h-px bg-[#D4AF37] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher-trigger"
              className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                scrolled
                  ? "text-[#2D3748] hover:text-[#0047AB]"
                  : "text-white hover:text-[#D4AF37]"
              }`}
            >
              <span className="flag" aria-hidden>
                {current.flag}
              </span>
              <span className="hidden sm:inline uppercase tracking-wider text-xs font-semibold">
                {current.code}
              </span>
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="lang-switcher-menu"
              className="min-w-[170px] rounded-none border-[#708090]/25"
            >
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`flex items-center gap-3 cursor-pointer rounded-none text-sm ${
                    lang === l.code ? "bg-[#D4AF37]/10 text-[#0047AB]" : ""
                  }`}
                >
                  <span className="flag">{l.flag}</span>
                  <span className="flex-1">{l.label}</span>
                  <span className="uppercase text-[10px] tracking-widest text-[#708090]">
                    {l.code}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Book table CTA */}
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-table-btn"
            className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-[var(--ms-blue-dark)] hover:bg-[#b5952f] transition-colors duration-300 px-5 py-2.5 uppercase tracking-wider text-[11px] font-semibold"
          >
            {t.nav.bookTable}
          </a>

          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-toggle"
            className={`xl:hidden p-2 ${scrolled ? "text-[#2D3748]" : "text-white"}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          id="mobile-navigation"
          className="xl:hidden glass-header border-t border-[#708090]/20 mt-3"
        >
          <nav className="max-w-[1600px] mx-auto px-6 py-5 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={`mobile-nav-${l.key}`}
                onClick={() => setMobileOpen(false)}
                className="text-base font-medium text-[#2D3748] py-1.5"
              >
                {t.nav[l.key]}
              </a>
            ))}
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-table-btn"
              className="mt-2 inline-flex justify-center items-center gap-2 bg-[#D4AF37] text-[var(--ms-blue-dark)] px-5 py-3 uppercase tracking-wider text-xs font-semibold"
            >
              {t.nav.bookTable}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
